import { type RefObject, useEffect, useRef } from 'react';

import { isClickInsideArea } from '../utils/clickOutsideUtils';

/**
 * Document-level outside click handler (capture phase).
 *
 * Useful when bubble-phase handlers are unreliable due to parent stopPropagation.
 */
export default function useOutsideClick(params: {
  isEnabled: boolean;
  containerRef: RefObject<HTMLDivElement | null>;
  ignoreSelectors?: readonly string[];
  onOutsideClick: () => void;
}): void {
  const {
    isEnabled,
    containerRef,
    ignoreSelectors = [],
    onOutsideClick,
  } = params;

  const onOutsideClickRef = useRef(onOutsideClick);

  useEffect(() => {
    onOutsideClickRef.current = onOutsideClick;
  }, [onOutsideClick]);

  useEffect(() => {
    if (!isEnabled) {
      return undefined;
    }

    const handleDocClick = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;

      if (!document.body.contains(target)) {
        return;
      }

      if (isClickInsideArea(target, containerRef, ignoreSelectors)) {
        return;
      }

      onOutsideClickRef.current();
    };

    document.addEventListener('mousedown', handleDocClick, true);
    document.addEventListener('touchstart', handleDocClick, true);

    return () => {
      document.removeEventListener('mousedown', handleDocClick, true);
      document.removeEventListener('touchstart', handleDocClick, true);
    };
  }, [isEnabled, containerRef, ignoreSelectors]);
}
